import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TInputAppState } from '../reducer';
import {
  TBulkCountEvent,
  TLastEventDetails,
  TLastEventInfoByBctx,
  TServerDefectivesDBLockedBy,
  TServerDefectivesLockResult,
} from '@monorepo/types';
import { Observable, of, Subject } from 'rxjs';
import {
  getAllActualsForAllBctx,
  getLockDetailsAndNextCacheResetTime,
  getMultipleRoundsDefectivesForCurrentLineAndShiftAndSelectedBctx,
  getPerPieceDefectivesForAllSubjectsAndInitStatus,
  getPerPieceDefectivesForCurrentLine,
  getPerPieceServerDefectivesForAllSubjects,
  getServerDefectivesForAllSubjects,
  getServerDefectivesPayload,
  hasServerDefectivesInitialized,
  latestServerDefectivesSyncTime,
} from './server-defectives-db.query';
import { map, switchMap, take } from 'rxjs/operators';
import {
  AcquireLockAndThenInitServerDefectives,
  AcquireLockAndThenLoadServerDefectives,
  UpdateServerDefectivesStoreLockDetails,
} from './server-defectives-db.action';
import produce from 'immer';
import { InputAppClientDBFacade } from '../input-app-client-db/input-app-client-db.facade';
import { EventUtil } from '@monorepo/utils';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class ServerDefectivesStoreFacade {
  private backfillMissingServerImagesBuckets = new Subject<void>();
  constructor(
    private store: Store<TInputAppState>,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private translate: TranslateService,
  ) {
    this.inputAppClientDBFacade.getLoadServerDefectivesSubscription().subscribe((loadWithoutLoader) => {
      this.reFetchServerDefectives(loadWithoutLoader);
    });
  }

  reFetchServerDefectives(loadWithoutLoader: boolean) {
    this.store.dispatch(new AcquireLockAndThenLoadServerDefectives('FETCHING_DEFECT_LIST', loadWithoutLoader));
  }

  init(initBy: TServerDefectivesDBLockedBy) {
    this.store.dispatch(new AcquireLockAndThenInitServerDefectives(initBy));
  }

  getLockDetailsAndNextCacheResetTime() {
    return this.store.pipe(select(getLockDetailsAndNextCacheResetTime));
  }

  getBackfillMissingServerImagesBucketsSubscription() {
    return this.backfillMissingServerImagesBuckets.asObservable();
  }

  emitBackfillMissingServerImagesBuckets() {
    this.backfillMissingServerImagesBuckets.next();
  }

  isServerDefectivesDBUnlocked() {
    return this.getLockDetailsAndNextCacheResetTime().pipe(
      take(1),
      map(({ lockDetails }) => !lockDetails.isLocked),
    );
  }

  acquireLock(lockFor: TServerDefectivesDBLockedBy): Observable<TServerDefectivesLockResult> {
    return this.getLockDetailsAndNextCacheResetTime().pipe(
      take(1),
      map(({ lockDetails, nextCacheResetTimestamp }) => {
        if (lockDetails.isLocked) {
          return {
            status: 'FAILURE',
            lockedBy: lockDetails.lockedBy,
            message: `${this.translate.instant('input_app_image_db_effects.please_wait')} (${lockDetails.lockedBy})`,
          };
        } else if (
          lockFor !== 'PRE_INIT_PENDING' &&
          lockFor !== 'DAY_END_RESET' &&
          nextCacheResetTimestamp < new Date().getTime()
        ) {
          return {
            status: 'FAILURE',
            lockedBy: lockDetails.lockedBy,
            message: this.translate.instant('server_defectives_db_facade.factoryEndTime'),
          };
        } else {
          this.store.dispatch(
            new UpdateServerDefectivesStoreLockDetails(
              produce(lockDetails, (draft) => {
                draft.isLocked = true;
                draft.lockedBy = lockFor;
              }),
            ),
          );
          return {
            status: 'SUCCESS',
            lockedBy: lockFor,
          };
        }
      }),
    );
  }

  getServerDefectivesPayload() {
    return this.store.pipe(select(getServerDefectivesPayload));
  }

  loadServerDefectives(updateActualsOnly: boolean) {
    this.inputAppClientDBFacade.fetchServerDefectivesAfterSyncingAllPendingEvents(updateActualsOnly, false);
  }

  getMultipleRoundsDefectivesForCurrentLineAndShiftAndSelectedBctx() {
    return this.store.pipe(select(getMultipleRoundsDefectivesForCurrentLineAndShiftAndSelectedBctx));
  }

  getAllActualsForAllBctx() {
    return this.store.pipe(select(getAllActualsForAllBctx));
  }

  getPerPieceDefectivesForCurrentLine() {
    return this.store.pipe(select(getPerPieceDefectivesForCurrentLine));
  }

  getServerDefectivesForAllSubjects() {
    return this.inputAppClientDBFacade.getAppIdentifier().pipe(
      take(1),
      switchMap((appIdentifier) => {
        if (appIdentifier === 'per_piece_qc_v2' || appIdentifier === 'md_point_per_piece_qc_v2')
          return this.store.pipe(select(getPerPieceServerDefectivesForAllSubjects));
        else return this.store.pipe(select(getServerDefectivesForAllSubjects));
      }),
    );
  }

  hasServerDefectivesInitialized() {
    return this.store.pipe(select(hasServerDefectivesInitialized));
  }

  getLatestServerDefectivesSyncTime() {
    return this.store.pipe(select(latestServerDefectivesSyncTime));
  }

  getStatusByBctxKey(bctxKey: string): Observable<TLastEventInfoByBctx> {
    return this.store.pipe(select(getPerPieceDefectivesForAllSubjectsAndInitStatus)).pipe(
      take(1),
      switchMap(({ defectives, hasInitialized }) => {
        const defectiveEvent = defectives.find((defective) => {
          if (defective.eventInfo.type === 'SERVER_DEFECTIVE')
            return defective.eventInfo.event.eventInfo.bctxDetails.bctxKey === bctxKey;
          else return defective.eventInfo.event.events[0].eventInfo.bctxDetails.bctxKey === bctxKey;
        });
        if (!hasInitialized) {
          return of({
            hasInitialized: hasInitialized,
            defectiveInfo: {
              isDefective: false,
            },
            lastEventInfo: {
              hasLastEvent: false,
            },
          } as TLastEventInfoByBctx);
        } else if (defectiveEvent) {
          return of({
            hasInitialized: true,
            defectiveInfo: {
              isDefective: true,
              defective: defectiveEvent,
            },
            lastEventInfo: {
              hasLastEvent: false,
            },
          } as TLastEventInfoByBctx);
        } else {
          return this.getPerPieceLastEventDetails(bctxKey).pipe(
            take(1),
            map((lastEventInfo) => {
              return {
                hasInitialized: true,
                lastEventInfo: {
                  hasLastEvent: true,
                  lastEvent: lastEventInfo,
                },
                defectiveInfo: {
                  isDefective: false,
                },
              } as TLastEventInfoByBctx;
            }),
          );
        }
      }),
    );
  }

  getPerPieceLastEventDetails(bctxKey: string): Observable<TLastEventDetails> {
    return this.inputAppClientDBFacade.getEventsArray().pipe(
      take(1),
      map((res) => {
        const filteredEvents: TBulkCountEvent[] = res.filter((bulkEvent) => {
          return (
            (bulkEvent.type === 'FORWARD_BULK_EVENT' || bulkEvent.type === 'UNDO_BULK_EVENT') &&
            bulkEvent.events[0].eventInfo.bctxDetails.bctxKey === bctxKey
          );
        }) as TBulkCountEvent[];
        if (filteredEvents.some((event) => event.backgroundMetadata.bulkEventStatus === 'SERVER_ERROR')) {
          return {
            hasServerError: true,
            remainingEvent: {
              event: null,
              modified_v_id: undefined,
            },
            firstEvent: null,
          };
        } else {
          const validEvents = EventUtil.getValidEventsFromLinkedEvents(filteredEvents);
          if (validEvents.length === 0) {
            return {
              hasServerError: false,
              remainingEvent: {
                event: null,
                modified_v_id: undefined,
              },
              firstEvent: null,
            };
          } else {
            const remainingEvent = EventUtil.getLastEventRemaining(
              validEvents.map((event) => {
                return {
                  eventInfo: event,
                  actionType: event.events[0].actionType,
                };
              }),
            );
            return {
              hasServerError: false,
              remainingEvent: {
                event: remainingEvent ? remainingEvent.eventInfo : null,
                modified_v_id: validEvents[validEvents.length - 1].events[0].eventInfo.v_id,
              },
              firstEvent: validEvents.length ? validEvents[0] : null,
            };
          }
        }
      }),
    );
  }
}
